import React, { useState, useEffect, Fragment } from 'react';
import styled from '@emotion/styled';
import { connect } from 'react-redux';
import { Row } from 'react-bootstrap';
import { MdAutoGraph } from 'react-icons/md';
import ComponentErrorBoundary from '@/Layouts/ComponentErrorBoundary';
import { fetchWrapper, getChannelIdFromSettingsURL, getTeamIdFromURL } from '@/_helpers';
import SingleChartContainer from './SingleChartContainer';
import { FullViewLoadingSpinner } from '@/_elements/Loaders';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import { getStartAndEndDate } from '../utils';

const DashboardContainer = styled.div`
    padding: 20px;
    margin: 0 20px;
    margin-top: 20px;
    background: white;
    border-radius: 10px;
    min-height: 300px;
`;

const ChartContainer = styled.div`
    margin-top: 20px;
    min-height: 400px;
`;

const EmptyChartPlaceholder = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 300px;
    background: #f8f9fa;
    border-radius: 10px;
    border: 2px dashed #dee2e6;
    margin-bottom: 20px;
`;

const Component = ({ title = 'Recent Analytics', mode, fetchToggle }) => {
    const [charts, setCharts] = useState([]);
    const [isBusy, setIsBusy] = useState(true);
    const fetchLlmAnalyticsCharts = async () => {
        try {
            setIsBusy(true);
            const teamId = getTeamIdFromURL();
            const channelId = getChannelIdFromSettingsURL();
            const response = await fetchWrapper.get(
                `/analytics/llm/dashboard-chart?${teamId ? `team=${teamId}` : ''}&${channelId ? `chatroom=${channelId}` : ''}${mode ? `&mode=${mode}` : ''}`
            );
            if (response?.results) {
                setCharts(response?.results);
            }
        } catch (err) {
            console.log(err);
        } finally {
            setIsBusy(false);
        }
    };
    useEffect(() => {
        fetchLlmAnalyticsCharts();
    }, [fetchToggle]);

    const handleDeleteChart = async (chart = {}) => {
        try {
            const result = await Swal.fire({
                title: `Are you sure you want to delete this chart?`,
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#3085d6',
                cancelButtonColor: '#d33',
                confirmButtonText: 'Yes',
            });

            if (result.isConfirmed) {
                const chartId = chart?._id;
                if (!chartId) {
                    throw new Error('No chart ID provided');
                }

                // await fetchWrapper.delete(`/analytics/llm/dashboard-chart/${chartId}`);
                await fetchWrapper.delete(`dashboard-charts/${chartId}`);
                await fetchLlmAnalyticsCharts();

                toast.success('Chart deleted successfully');
            }
        } catch (error) {
            console.error('Error deleting chart:', error);
            toast.error(error?.message || 'Failed to delete chart');
        }
    };
    const handleUpdateChart = async (chart = {}, payload) => {
        try {
            const chartId = chart?._id;
            if (!chartId) {
                throw new Error('No chart ID provided');
            }
            const updatedPayload = { ...payload, ...getStartAndEndDate(payload.timeRange) };
            // await fetchWrapper.put(`/analytics/llm/dashboard-chart/${chartId}`, payload);
            await fetchWrapper.put(`dashboard-charts/${chartId}`, updatedPayload);
            await fetchLlmAnalyticsCharts();
            toast.success('Chart Updated successfully');
        } catch (error) {
            console.error('Error updating chart:', error);
            toast.error(error?.message || 'Failed to update chart');
        }
    };
    return (
        <DashboardContainer>
            <h2 className="mb-4">{mode === 'edit' ? `Saved Charts (${(charts || []).length}/6)` : title}</h2>
            {isBusy ? (
                <FullViewLoadingSpinner />
            ) : (
                <Fragment>
                    {charts && charts?.length > 0 ? (
                        <ChartContainer>
                            <Row>
                                {charts.map((chart, index) => (
                                    <SingleChartContainer
                                        key={chart?.id || index}
                                        chart={chart}
                                        mode={mode}
                                        handleDeleteChart={handleDeleteChart}
                                        handleUpdateChart={handleUpdateChart}
                                    />
                                ))}
                            </Row>
                        </ChartContainer>
                    ) : (
                        <EmptyChartPlaceholder>
                            <MdAutoGraph size={48} className="text-muted mb-3" />
                            <h5 className="text-muted">No charts yet</h5>
                            <p className="text-muted">Use the prompt above to ask BobBot to create charts for you</p>
                        </EmptyChartPlaceholder>
                    )}
                </Fragment>
            )}
        </DashboardContainer>
    );
};

const mapStateToProps = (state) => ({
    teams: state.teams.teams,
    currUser: state.user.user,
});

const ChartsWithValidation = (props) => {
    return (
        <ComponentErrorBoundary>
            <Component {...props} />
        </ComponentErrorBoundary>
    );
};

export default connect(mapStateToProps)(ChartsWithValidation);
